import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { GraduationCap, KeyRound, LogIn, UserPlus, Loader2, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { UserHeader } from '../../components/UserHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CampusAcceso() {
  const [searchParams] = useSearchParams();
  const codigoInicial = searchParams.get('codigo') || '';
  const emailInicial = searchParams.get('email') || '';
  const tabInicial = searchParams.get('tab') === 'registro' ? 'registro' : 'login';
  const navigate = useNavigate();
  const { loginExterno: loginExternoCtx } = useAuth();

  const [tab, setTab] = useState(tabInicial); // login | registro
  const [loginForm, setLoginForm] = useState({ email: emailInicial, password: '' });
  const [regForm, setRegForm] = useState({
    nombre: '', apellido: '', email: emailInicial, password: '', dni: '', telefono: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');     // mensaje verde post-registro
  const [showReenviar, setShowReenviar] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // Check if already logged in
  const token = localStorage.getItem('token');
  if (token) {
    const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
    if (redirectUrl && redirectUrl !== '/login') {
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirectUrl, { replace: true });
    } else {
      navigate(codigoInicial ? `/campus/unirse?codigo=${codigoInicial}` : '/campus');
    }
    return null;
  }

  const loginExterno = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) return;
    setLoading(true);
    setError('');
    setShowReenviar(false);
    try {
      // Usar el helper del AuthContext: actualiza token/user en memoria
      await loginExternoCtx(loginForm.email, loginForm.password);

      // Si había un redirect pendiente (ej: invitación por email), respetarlo
      const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
      let destino;
      if (redirectUrl && redirectUrl !== '/login') {
        sessionStorage.removeItem('redirectAfterLogin');
        destino = redirectUrl;
      } else {
        destino = codigoInicial ? `/campus/unirse?codigo=${codigoInicial}` : '/campus';
      }
      navigate(destino, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al iniciar sesión';
      setError(detail);
      // Si el error es por email no verificado, ofrecer reenvío
      if (err.response?.status === 403 && /verificar/i.test(detail)) {
        setShowReenviar(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const reenviarVerificacion = async () => {
    if (!loginForm.email) {
      setError('Ingresá tu email arriba primero.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/campus/auth/reenviar-verificacion`, { email: loginForm.email });
      setError('');
      setSuccessMsg(res.data?.message || 'Te enviamos un nuevo link de verificación.');
      setShowReenviar(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'No pudimos reenviar el email.');
    } finally {
      setLoading(false);
    }
  };

  const registrarExterno = async (e) => {
    e.preventDefault();
    const f = regForm;
    if (!f.nombre || !f.apellido || !f.email || !f.password || !f.dni || !f.telefono) {
      setError('Completá todos los campos obligatorios');
      return;
    }
    if (f.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/campus/auth/registro-externo`, f);
      setSuccessMsg(res.data?.message || 'Te enviamos un email para verificar tu cuenta.');
      setTab('login');
      setLoginForm({ email: f.email, password: '' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(d => d.msg).join(' · '));
      } else {
        setError(detail || 'Error al registrarse');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <UserHeader />
      <div className="py-8 px-4">
        <div className="max-w-md mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </button>

          <Card>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <GraduationCap className="w-8 h-8 text-violet-600" />
              </div>
              <CardTitle>Acceso al Campus</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                {codigoInicial
                  ? 'Ingresá o registrate para unirte al espacio'
                  : 'Ingresá o registrate para acceder al Campus CATSLZ'
                }
              </p>
              {codigoInicial && (
                <div className="flex items-center justify-center gap-2 mt-2">
                  <KeyRound className="w-4 h-4 text-violet-600" />
                  <span className="font-mono text-sm font-bold text-violet-600">{codigoInicial}</span>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <a href="/login" className="flex items-center justify-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-4 py-3 mb-6 hover:bg-violet-100 transition-colors">
                <LogIn className="w-4 h-4 text-violet-600" />
                <span className="text-sm text-violet-700 font-medium">¿Sos matriculado/a? Ingresá con tu cuenta del portal</span>
              </a>

              {/* Separador visual destacado para externos */}
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-xs text-gray-400 uppercase tracking-wider">o</span>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <UserPlus className="w-5 h-5 text-amber-700 flex-shrink-0" />
                  <h2 className="text-base font-bold text-amber-900" data-testid="externos-titulo">
                    Cursantes externos al CATSLZ
                  </h2>
                </div>
                <p className="text-xs text-amber-800 leading-snug">
                  ¿No sos matriculade del Colegio? Creá una cuenta de cursante externe para acceder a cursos abiertos del Campus.
                </p>
              </div>

              <div className="flex border-b mb-4">
                <button
                  onClick={() => { setTab('login'); setError(''); setSuccessMsg(''); }}
                  className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                    tab === 'login' ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500'
                  }`}
                  data-testid="tab-login-externo"
                >
                  <LogIn className="w-4 h-4 inline mr-1" />
                  Ya tengo cuenta
                </button>
                <button
                  onClick={() => { setTab('registro'); setError(''); setSuccessMsg(''); }}
                  className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                    tab === 'registro' ? 'border-violet-600 text-violet-600' : 'border-transparent text-gray-500'
                  }`}
                  data-testid="tab-registro-externo"
                >
                  <UserPlus className="w-4 h-4 inline mr-1" />
                  Registrarme
                </button>
              </div>

              {successMsg && (
                <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 p-3 rounded-md mb-3" data-testid="acceso-success">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{successMsg}</span>
                </div>
              )}

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md mb-3" data-testid="acceso-error">
                  {error}
                  {showReenviar && (
                    <button
                      type="button"
                      onClick={reenviarVerificacion}
                      className="mt-2 inline-flex items-center gap-1 text-red-700 underline font-medium"
                      data-testid="reenviar-verificacion-btn"
                    >
                      <Mail className="w-4 h-4" />
                      Reenviarme el link de verificación
                    </button>
                  )}
                </div>
              )}

              {/* Login form */}
              {tab === 'login' && (
                <form onSubmit={loginExterno} className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">Email</label>
                    <Input type="email" placeholder="tu@email.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      data-testid="login-externo-email" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Contraseña</label>
                    <Input type="password" placeholder="Tu contraseña"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      data-testid="login-externo-password" />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-externo-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                    Ingresar
                  </Button>
                  <div className="text-center pt-1">
                    <button
                      type="button"
                      onClick={() => setShowForgot(true)}
                      className="text-xs text-violet-600 hover:text-violet-800 hover:underline"
                      data-testid="forgot-password-externo-btn"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>
                </form>
              )}

              {/* Forgot password (externos): solicitar link de recupero */}
              {showForgot && (
                <ForgotPasswordModal
                  initialEmail={loginForm.email}
                  onClose={() => setShowForgot(false)}
                />
              )}

              {/* Register form */}
              {tab === 'registro' && (
                <form onSubmit={registrarExterno} className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-sm font-medium">Nombre *</label>
                      <Input placeholder="Nombre"
                        value={regForm.nombre}
                        onChange={(e) => setRegForm({ ...regForm, nombre: e.target.value })}
                        data-testid="registro-nombre" />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Apellido *</label>
                      <Input placeholder="Apellido"
                        value={regForm.apellido}
                        onChange={(e) => setRegForm({ ...regForm, apellido: e.target.value })}
                        data-testid="registro-apellido" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">DNI *</label>
                    <Input placeholder="12345678"
                      value={regForm.dni}
                      onChange={(e) => setRegForm({ ...regForm, dni: e.target.value.replace(/\D/g, '') })}
                      maxLength={9}
                      data-testid="registro-dni" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Teléfono de contacto *</label>
                    <Input placeholder="1144556677"
                      value={regForm.telefono}
                      onChange={(e) => setRegForm({ ...regForm, telefono: e.target.value })}
                      data-testid="registro-telefono" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Email *</label>
                    <Input type="email" placeholder="tu@email.com"
                      value={regForm.email}
                      onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                      data-testid="registro-email" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Contraseña *</label>
                    <Input type="password" placeholder="Mínimo 6 caracteres"
                      value={regForm.password}
                      onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                      data-testid="registro-password" />
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Después de registrarte vas a recibir un email para verificar tu cuenta.
                    El link tiene validez de 48 horas.
                  </p>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="registro-externo-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                    Crear cuenta
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ===== Modal: solicitar link de recuperación de contraseña =====
function ForgotPasswordModal({ initialEmail = '', onClose }) {
  const [email, setEmail] = useState(initialEmail);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const enviar = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setErr('');
    try {
      await axios.post(`${API_URL}/auth/recuperar-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'No pudimos procesar tu solicitud. Reintentá en unos minutos.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}
        data-testid="forgot-password-modal">
        {sent ? (
          <div className="text-center space-y-3">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <h3 className="font-bold text-lg">Revisá tu casilla</h3>
            <p className="text-sm text-gray-600">
              Si el email <strong>{email}</strong> está registrado, te llegó un link para crear una nueva contraseña.
              El link expira en 1 hora.
            </p>
            <p className="text-xs text-gray-400">Si no lo ves, revisá la carpeta de spam.</p>
            <Button onClick={onClose} className="w-full mt-2">Entendido</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <KeyRound className="w-5 h-5 text-violet-600" />
              <h3 className="font-bold text-lg">Recuperar contraseña</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Ingresá tu email y te enviaremos un link para crear una nueva contraseña.
            </p>
            <form onSubmit={enviar} className="space-y-3">
              <Input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={sending}
                autoFocus
                data-testid="forgot-password-email-input"
              />
              {err && <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{err}</p>}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={sending}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1" disabled={sending || !email.trim()}
                  data-testid="forgot-password-submit-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Enviar link
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
